(() => {
  const DESKTOP_QUERY = "(min-width: 1024px)";
  const CLOSE_DELAY = 90;
  const CONTROLLER_KEY = "__tanatelDesktopMenuModernController";

  const initDesktopMenu = (header) => {
    if (!header) return;

    const existingController = window[CONTROLLER_KEY];
    if (
      existingController?.header === header &&
      header.dataset.desktopMenuModernReady === "true"
    ) {
      return;
    }

    // Shopify Theme Editor can replace the Header section without reloading the
    // iframe. Destroy the previous controller and remove every orphaned portal
    // before binding a new one. Otherwise a stale body-level portal can remain
    // visible while the new controller is trying to close a different portal.
    existingController?.destroy?.();
    document
      .querySelectorAll(".tanatel-desktop-submenu-portal")
      .forEach((portal) => portal.remove());

    const lifecycle = new AbortController();
    const lifecycleSignal = lifecycle.signal;
    let destroyed = false;

    const desktopHeader = header.querySelector(".m-header__desktop");
    const topLevelItems = [
      ...(desktopHeader?.querySelectorAll(".m-menu > .m-menu__item") || []),
    ];
    const parentItems = topLevelItems.filter(
      (item) =>
        item.classList.contains("m-menu__item--parent") &&
        item.querySelector(":scope > .m-mega-menu")
    );

    if (!desktopHeader || !parentItems.length) return;

    header.dataset.desktopMenuModernReady = "true";

    let activeItem = null;
    let closeTimer = null;
    let activeNested = null;

    /*
     * Level three must not stay inside the theme dropdown. The theme applies
     * competing left/right/transform rules to that descendant, and transformed
     * ancestors also create an unreliable positioning context. Render a clean
     * desktop-only copy in a body-level fixed portal instead.
     */
    const nestedPortal = document.createElement("div");
    nestedPortal.className = "tanatel-desktop-submenu-portal";
    nestedPortal.id = `TanatelDesktopSubmenu-${Math.random()
      .toString(36)
      .slice(2, 9)}`;
    nestedPortal.hidden = true;
    nestedPortal.setAttribute("aria-hidden", "true");
    document.body.append(nestedPortal);

    const hideNestedPortal = () => {
      // Reset *all* nested state, not just activeNested. This deliberately cleans
      // stale classes left by an interrupted Theme Editor section reload.
      parentItems.forEach((parentItem) => {
        parentItem.classList.remove("tanatel-third-level-open");
        parentItem
          .querySelectorAll(".tanatel-submenu-active")
          .forEach((child) => child.classList.remove("tanatel-submenu-active"));
        parentItem
          .querySelectorAll("[data-tanatel-submenu-trigger]")
          .forEach((trigger) => trigger.setAttribute("aria-expanded", "false"));
      });

      activeNested = null;
      nestedPortal.classList.remove("is-open", "is-flipped");
      nestedPortal.hidden = true;
      nestedPortal.setAttribute("aria-hidden", "true");
      nestedPortal.replaceChildren();
    };

    const positionNestedPortal = () => {
      if (!activeNested || nestedPortal.hidden) return;

      const triggerRect = activeNested.trigger.getBoundingClientRect();
      const portalRect = nestedPortal.getBoundingClientRect();
      const viewportPadding = 16;
      const gap = 10;
      const isRtl =
        getComputedStyle(activeNested.trigger).direction === "rtl";

      let left = isRtl
        ? triggerRect.left - portalRect.width - gap
        : triggerRect.right + gap;
      let flipped = false;

      if (left < viewportPadding) {
        left = triggerRect.right + gap;
        flipped = isRtl;
      }

      if (left + portalRect.width > window.innerWidth - viewportPadding) {
        left = triggerRect.left - portalRect.width - gap;
        flipped = !isRtl;
      }

      left = Math.min(
        Math.max(left, viewportPadding),
        Math.max(viewportPadding, window.innerWidth - portalRect.width - viewportPadding)
      );

      const maxTop = Math.max(
        viewportPadding,
        window.innerHeight - portalRect.height - viewportPadding
      );
      const top = Math.min(Math.max(triggerRect.top, viewportPadding), maxTop);

      nestedPortal.style.left = `${Math.round(left)}px`;
      nestedPortal.style.top = `${Math.round(top)}px`;
      nestedPortal.classList.toggle("is-flipped", flipped);
    };

    const showNestedPortal = (item, child, trigger, column, submenu) => {
      if (destroyed || !window.matchMedia(DESKTOP_QUERY).matches) return;

      if (activeNested?.child !== child) {
        activeNested?.child.classList.remove("tanatel-submenu-active");
        activeNested?.trigger.setAttribute("aria-expanded", "false");
      }

      const levelTwoList = column.querySelector(".m-sub-menu--level-2");
      if (!levelTwoList) return;

      activeNested = { item, child, trigger, column };
      child.classList.add("tanatel-submenu-active");
      trigger.setAttribute("aria-expanded", "true");
      item.classList.add("tanatel-third-level-open");

      nestedPortal.className = "tanatel-desktop-submenu-portal";
      [...submenu.classList]
        .filter((className) => className.startsWith("m-color-"))
        .forEach((className) => nestedPortal.classList.add(className));
      nestedPortal.dir = getComputedStyle(trigger).direction;
      nestedPortal.replaceChildren(levelTwoList.cloneNode(true));
      nestedPortal.hidden = false;
      nestedPortal.setAttribute("aria-hidden", "false");

      window.requestAnimationFrame(() => {
        if (activeNested?.child !== child) return;
        positionNestedPortal();
        nestedPortal.classList.add("is-open");
      });
    };

    /*
     * The theme's SiteNav owns the same hover classes and clears them on an
     * independent timeout. Keep its mobile/slider code, but hand desktop hover
     * state to this controller so transitions between sibling items are atomic.
     */
    const legacySiteNav = header.siteNav;
    if (legacySiteNav) {
      clearTimeout(legacySiteNav.timeoutEnter);
      clearTimeout(legacySiteNav.timeoutLeave);
      legacySiteNav.activeIndex = -1;
      legacySiteNav.onMenuItemEnter = () => {};
      legacySiteNav.onMenuItemLeave = () => {};
    }

    const cancelClose = () => {
      if (closeTimer === null) return;
      window.clearTimeout(closeTimer);
      closeTimer = null;
    };

    const setHeaderOpen = (isOpen) => {
      header.classList.toggle("tanatel-desktop-menu-open", isOpen);
      header.classList.toggle("m-mega-active", isOpen);
    };

    const closeItem = (item) => {
      if (!item) return;
      if (activeNested?.item === item) hideNestedPortal();
      item.classList.remove("m-menu__item--active");
      item.classList.remove("tanatel-third-level-open");
      item
        .querySelectorAll(".tanatel-submenu-active")
        .forEach((child) => child.classList.remove("tanatel-submenu-active"));
      item
        .querySelectorAll(".m-sub-menu__item--submenu-flipped")
        .forEach((child) =>
          child.classList.remove("m-sub-menu__item--submenu-flipped")
        );
      item
        .querySelectorAll("[data-tanatel-submenu-trigger]")
        .forEach((trigger) => trigger.setAttribute("aria-expanded", "false"));
      item
        .querySelector(":scope > .m-menu__link--main")
        ?.setAttribute("aria-expanded", "false");
    };

    const updateDropdownMetrics = (item) => {
      const submenu = item?.querySelector(":scope > .m-mega-menu");
      if (!submenu) return;

      submenu.style.setProperty("--tanatel-dropdown-shift", "0px");

      window.requestAnimationFrame(() => {
        if (activeItem !== item) return;
        const rect = submenu.getBoundingClientRect();
        const viewportPadding = 16;
        let shift = 0;

        if (rect.left < viewportPadding) {
          shift = viewportPadding - rect.left;
        } else if (rect.right > window.innerWidth - viewportPadding) {
          shift = window.innerWidth - viewportPadding - rect.right;
        }

        submenu.style.setProperty(
          "--tanatel-dropdown-shift",
          `${Math.round(shift)}px`
        );

        window.requestAnimationFrame(() => {
          if (activeItem !== item) return;
          const correctedRect = submenu.getBoundingClientRect();
          header.style.setProperty(
            "--sf-dropdown-width",
            `${correctedRect.width}px`
          );
          header.style.setProperty(
            "--sf-dropdown-height",
            `${correctedRect.height}px`
          );
        });
      });
    };

    const openItem = (item) => {
      if (destroyed || !window.matchMedia(DESKTOP_QUERY).matches) return;

      cancelClose();

      if (activeItem !== item) {
        closeItem(activeItem);
        activeItem = item;
      }

      item.classList.add("m-menu__item--active");
      item
        .querySelector(":scope > .m-menu__link--main")
        ?.setAttribute("aria-expanded", "true");

      /*
       * Keep the backdrop state set during a direct sibling-to-sibling switch.
       * It is intentionally independent from the theme's legacy hover timeout.
       */
      setHeaderOpen(true);
      legacySiteNav?.reInitSlider?.(item);
      updateDropdownMetrics(item);
    };

    const closeAll = () => {
      cancelClose();
      parentItems.forEach(closeItem);
      activeItem = null;
      setHeaderOpen(false);
    };

    const scheduleClose = (delay = CLOSE_DELAY) => {
      cancelClose();
      closeTimer = window.setTimeout(() => {
        closeTimer = null;
        closeAll();
      }, delay);
    };

    /*
     * mouseleave/relatedTarget is not reliable when the pointer is moved very
     * quickly across separate DOM trees (header -> body portal -> page).
     * Track the real pointer coordinates and keep level three open only while
     * the pointer is over the source row, the portal, or the small corridor
     * connecting the two. This also guarantees stale portal state is cleared.
     */
    const pointInsideRect = (x, y, rect, padding = 0) =>
      x >= rect.left - padding &&
      x <= rect.right + padding &&
      y >= rect.top - padding &&
      y <= rect.bottom + padding;

    const pointerInsideActiveMenuSafeArea = (x, y) => {
      if (!activeItem) return false;

      const trigger = activeItem.querySelector(":scope > .m-menu__link--main");
      const submenu = activeItem.querySelector(":scope > .m-mega-menu");
      if (!trigger || !submenu) return false;

      const triggerRect = trigger.getBoundingClientRect();
      const submenuRect = submenu.getBoundingClientRect();

      // Normal hit areas.
      if (pointInsideRect(x, y, triggerRect, 4)) return true;
      if (pointInsideRect(x, y, submenuRect, 4)) return true;

      // Build a narrow vertical bridge between the top-level trigger and its
      // dropdown. This covers the visual offset/gap used by the modern header
      // (for example top: calc(100% + 16px)) without treating the whole header
      // as safe. The bridge is deliberately limited to the horizontal span of
      // the trigger/dropdown so moving toward logo/actions still closes.
      const gapTop = Math.min(triggerRect.bottom, submenuRect.top) - 6;
      const gapBottom = Math.max(triggerRect.bottom, submenuRect.top) + 6;
      const bridgeLeft = Math.min(triggerRect.left, submenuRect.left) - 8;
      const bridgeRight = Math.max(triggerRect.right, submenuRect.right) + 8;

      if (
        submenuRect.top >= triggerRect.bottom &&
        x >= bridgeLeft &&
        x <= bridgeRight &&
        y >= gapTop &&
        y <= gapBottom
      ) {
        return true;
      }

      // Defensive mirror for layouts where the dropdown is rendered above.
      const upperGapTop = Math.min(submenuRect.bottom, triggerRect.top) - 6;
      const upperGapBottom = Math.max(submenuRect.bottom, triggerRect.top) + 6;
      if (
        submenuRect.bottom <= triggerRect.top &&
        x >= bridgeLeft &&
        x <= bridgeRight &&
        y >= upperGapTop &&
        y <= upperGapBottom
      ) {
        return true;
      }

      return false;
    };

    const pointerInsideNestedSafeArea = (x, y) => {
      if (!activeNested || nestedPortal.hidden) return false;

      const sourceRect = activeNested.child.getBoundingClientRect();
      const portalRect = nestedPortal.getBoundingClientRect();

      if (pointInsideRect(x, y, sourceRect, 4)) return true;
      if (pointInsideRect(x, y, portalRect, 4)) return true;

      const corridor = {
        left: Math.min(sourceRect.right, portalRect.right, sourceRect.left, portalRect.left),
        right: Math.max(sourceRect.left, portalRect.left, sourceRect.right, portalRect.right),
        top: Math.min(sourceRect.top, portalRect.top) - 6,
        bottom: Math.max(sourceRect.bottom, portalRect.bottom) + 6,
      };

      // Restrict the bridge to the horizontal gap between source and portal.
      if (portalRect.right <= sourceRect.left) {
        corridor.left = portalRect.right - 4;
        corridor.right = sourceRect.left + 4;
      } else if (portalRect.left >= sourceRect.right) {
        corridor.left = sourceRect.right - 4;
        corridor.right = portalRect.left + 4;
      } else {
        return false;
      }

      return pointInsideRect(x, y, corridor);
    };

    let lastPointerX = -1;
    let lastPointerY = -1;
    let pointerFrame = 0;

    const reconcileNestedPointerState = () => {
      pointerFrame = 0;
      if (destroyed || !activeNested) return;
      if (pointerInsideNestedSafeArea(lastPointerX, lastPointerY)) return;

      const currentItem = activeNested.item;
      hideNestedPortal();

      // If the pointer also left the whole top-level item, close the parent
      // immediately. Moving onto another row is handled by its mouseenter.
      const hoveredElement = document.elementFromPoint(lastPointerX, lastPointerY);
      if (!hoveredElement || !currentItem.contains(hoveredElement)) {
        scheduleClose(0);
      }
    };

    document.addEventListener(
      "pointermove",
      (event) => {
        if (event.pointerType === "touch") return;

        lastPointerX = event.clientX;
        lastPointerY = event.clientY;

        /*
         * The desktop header itself is NOT a safe hover zone. Previously
         * desktopHeader mouseenter cancelled the close timer, so a fast move
         * from an open menu into the logo/actions/empty header space could
         * leave the dropdown stuck open for as long as the pointer remained
         * anywhere inside the header. Reconcile against the element actually
         * under the pointer on every animation frame.
         */
        const hoveredElement = document.elementFromPoint(
          event.clientX,
          event.clientY
        );

        if (activeItem && hoveredElement) {
          const hoveredTopLevelItem = hoveredElement.closest?.(
            ".m-menu > .m-menu__item"
          );

          // A direct jump to another parent item should switch atomically even
          // when mouseenter is skipped/coalesced during a very fast movement.
          if (
            hoveredTopLevelItem &&
            parentItems.includes(hoveredTopLevelItem) &&
            hoveredTopLevelItem !== activeItem
          ) {
            openItem(hoveredTopLevelItem);
          } else {
            const insideActiveItem = activeItem.contains(hoveredElement);
            const insideActiveMenuBridge = pointerInsideActiveMenuSafeArea(
              event.clientX,
              event.clientY
            );
            const insidePortal =
              !nestedPortal.hidden && nestedPortal.contains(hoveredElement);
            const insideNestedBridge =
              activeNested &&
              pointerInsideNestedSafeArea(event.clientX, event.clientY);

            // The whole header is not safe, but the intentional visual gap
            // between the active top-level trigger and its dropdown is. This
            // lets the pointer travel into the submenu without racing an
            // immediate close, while logo/actions/empty header space still
            // close the menu.
            if (
              desktopHeader.contains(hoveredElement) &&
              !insideActiveItem &&
              !insideActiveMenuBridge &&
              !insidePortal &&
              !insideNestedBridge
            ) {
              scheduleClose(70);
              return;
            }

            if (insideActiveItem || insideActiveMenuBridge || insidePortal || insideNestedBridge) {
              cancelClose();
            }
          }
        }

        if (!activeNested) return;
        if (pointerFrame) return;
        pointerFrame = window.requestAnimationFrame(reconcileNestedPointerState);
      },
      { passive: true, signal: lifecycleSignal }
    );

    parentItems.forEach((item, index) => {
      const trigger = item.querySelector(":scope > .m-menu__link--main");
      const submenu = item.querySelector(":scope > .m-mega-menu");

      if (!trigger || !submenu) return;

      const submenuId = submenu.id || `TanatelDesktopMenu-${index + 1}`;
      submenu.id = submenuId;
      trigger.setAttribute("aria-haspopup", "true");
      trigger.setAttribute("aria-controls", submenuId);
      trigger.setAttribute("aria-expanded", "false");
      submenu.addEventListener("transitionend", (event) => {
        if (event.propertyName !== "width" || activeItem !== item) return;
        updateDropdownMetrics(item);
      });

      const levelOneList = submenu.querySelector(".m-sub-menu--level-1");
      const levelOneItems = [
        ...(levelOneList?.querySelectorAll(
          ":scope > .m-sub-menu__item--level-1"
        ) || []),
      ];
      const nestedItems = levelOneItems.filter((child) =>
        child.querySelector(":scope > .m-mega-menu__column")
      );

      if (
        item.classList.contains("m-menu__item--dropdown") &&
        nestedItems.length
      ) {
        item.classList.add("tanatel-has-third-level");

        const clearNestedItems = () => {
          item.classList.remove("tanatel-third-level-open");
          if (activeNested?.item === item) hideNestedPortal();
          nestedItems.forEach((child) => {
            child.classList.remove("tanatel-submenu-active");
            child.classList.remove("m-sub-menu__item--submenu-flipped");
            child
              .querySelector(":scope > .m-menu__link")
              ?.setAttribute("aria-expanded", "false");
          });
        };

        const updateStageHeight = () => {
          updateDropdownMetrics(item);
        };

        levelOneItems.forEach((child, childIndex) => {
          const childTrigger = child.querySelector(":scope > .m-menu__link");
          const column = child.querySelector(
            ":scope > .m-mega-menu__column"
          );

          if (!childTrigger || !column) {
            child.addEventListener("mouseenter", () => {
              clearNestedItems();
              updateStageHeight();
            });
            child.addEventListener("focusin", () => {
              clearNestedItems();
              updateStageHeight();
            });
            return;
          }

          const columnId =
            column.id ||
            `${submenuId}-Level3-${childIndex + 1}`;
          column.id = columnId;
          column.classList.add("tanatel-submenu-source");
          childTrigger.dataset.tanatelSubmenuTrigger = "true";
          childTrigger.setAttribute("aria-haspopup", "true");
          childTrigger.setAttribute("aria-controls", nestedPortal.id);
          childTrigger.setAttribute("aria-expanded", "false");
          const arrow = childTrigger.querySelector(".m-menu__sub-arrow");

          arrow?.addEventListener("pointerdown", () => {
            arrow.dataset.tanatelWasOpen = child.classList.contains(
              "tanatel-submenu-active"
            )
              ? "true"
              : "false";
          });

          const activateNestedItem = () => {
            nestedItems.forEach((candidate) => {
              if (candidate === child) return;
              candidate.classList.remove("tanatel-submenu-active");
              candidate
                .querySelector(":scope > .m-menu__link")
                ?.setAttribute("aria-expanded", "false");
            });
            item.classList.add("tanatel-third-level-open");
            showNestedPortal(item, child, childTrigger, column, submenu);
            updateStageHeight();
          };

          child.addEventListener("mouseenter", activateNestedItem);
          child.addEventListener("focusin", activateNestedItem);
          childTrigger.addEventListener("click", (event) => {
            if (!event.target.closest(".m-menu__sub-arrow")) return;

            event.preventDefault();
            event.stopPropagation();

            const wasOpen =
              arrow?.dataset.tanatelWasOpen === "true";
            arrow?.removeAttribute("data-tanatel-was-open");

            if (wasOpen) {
              clearNestedItems();
              updateStageHeight();
              return;
            }

            activateNestedItem();
          });
        });

        levelOneList.addEventListener("mouseleave", (event) => {
          if (nestedPortal.contains(event.relatedTarget)) return;
          if (!item.matches(":hover")) scheduleClose();
        });

        window.requestAnimationFrame(() => updateStageHeight());
      }

      item.addEventListener("mouseenter", () => openItem(item));
      item.addEventListener("focusin", () => openItem(item));
    });

    /*
     * A top-level link without children must close an already-open menu.
     * Parent-to-parent movement never closes the shared backdrop.
     */
    topLevelItems
      .filter((item) => !parentItems.includes(item))
      .forEach((item) => {
        item.addEventListener("mouseenter", () => scheduleClose(40));
        item.addEventListener("focusin", closeAll);
      });

    desktopHeader.addEventListener("mouseenter", (event) => {
      // Only cancel a pending close when re-entering the active menu itself.
      // Merely being somewhere inside the header must never keep a stale menu
      // open. pointermove above performs the authoritative hit-test.
      if (activeItem?.contains(event.target)) cancelClose();
    });
    desktopHeader.addEventListener("mouseleave", () => scheduleClose());
    nestedPortal.addEventListener("mouseenter", cancelClose);
    nestedPortal.addEventListener("mouseleave", () => {
      hideNestedPortal();
      scheduleClose(0);
    });
    nestedPortal.addEventListener("focusin", cancelClose);
    nestedPortal.addEventListener("focusout", () => {
      window.requestAnimationFrame(() => {
        if (nestedPortal.contains(document.activeElement)) return;
        scheduleClose(0);
      });
    });
    desktopHeader.addEventListener("focusout", () => {
      window.requestAnimationFrame(() => {
        if (
          desktopHeader.contains(document.activeElement) ||
          nestedPortal.contains(document.activeElement)
        ) {
          return;
        }
        scheduleClose(0);
      });
    });

    document.addEventListener(
      "click",
      (event) => {
        if (!activeItem) return;
        if (desktopHeader.contains(event.target) || nestedPortal.contains(event.target)) return;
        closeAll();
      },
      { signal: lifecycleSignal }
    );

    desktopHeader.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !activeItem) return;

      const trigger = activeItem.querySelector(
        ":scope > .m-menu__link--main"
      );
      event.preventDefault();
      closeAll();
      trigger?.focus();
    });

    const desktopMedia = window.matchMedia(DESKTOP_QUERY);
    desktopMedia.addEventListener?.(
      "change",
      (event) => {
        if (!event.matches) closeAll();
      },
      { signal: lifecycleSignal }
    );

    window.addEventListener(
      "resize",
      () => {
        if (!activeItem) return;
        updateDropdownMetrics(activeItem);
        positionNestedPortal();
      },
      { signal: lifecycleSignal }
    );
    window.addEventListener("scroll", positionNestedPortal, {
      passive: true,
      signal: lifecycleSignal,
    });

    const closeFromViewportExit = (event) => {
      // Crossing from the storefront iframe into Shopify's editor chrome can
      // stop pointermove delivery. relatedTarget === null is the reliable exit
      // signal in that case, so close synchronously rather than waiting.
      if (event && event.relatedTarget) return;
      hideNestedPortal();
      closeAll();
    };

    document.documentElement.addEventListener(
      "mouseleave",
      closeFromViewportExit,
      { signal: lifecycleSignal }
    );
    window.addEventListener("mouseout", closeFromViewportExit, {
      signal: lifecycleSignal,
    });
    window.addEventListener(
      "pointercancel",
      () => {
        hideNestedPortal();
        closeAll();
      },
      { signal: lifecycleSignal }
    );
    window.addEventListener(
      "blur",
      () => {
        hideNestedPortal();
        closeAll();
      },
      { signal: lifecycleSignal }
    );
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.hidden) {
          hideNestedPortal();
          closeAll();
        }
      },
      { signal: lifecycleSignal }
    );

    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      cancelClose();
      hideNestedPortal();
      setHeaderOpen(false);
      lifecycle.abort();
      nestedPortal.remove();
      header.removeAttribute("data-desktop-menu-modern-ready");
      if (window[CONTROLLER_KEY]?.header === header) {
        delete window[CONTROLLER_KEY];
      }
    };

    window[CONTROLLER_KEY] = { header, destroy };
  };

  const initAll = () => {
    document.querySelectorAll("m-header").forEach(initDesktopMenu);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", initAll);
  document.addEventListener("shopify:section:unload", (event) => {
    const controller = window[CONTROLLER_KEY];
    if (!controller) return;
    if (event.target?.contains?.(controller.header) || controller.header === event.target) {
      controller.destroy();
    }
  });
})();
